/**
 * Hook for managing the agent output panel state: logs, streaming text,
 * persisted ephemeral entries, pipeline tabs, historical job tabs,
 * panel resize, and auto-scroll.
 */
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { AgentLogEntry, JobStatus } from '../types';

export interface PersistedEphemeralEntry {
  id: number;
  timestamp: Date;
  text: string;
  isExpanded: boolean;
}

export const useAgentPanel = () => {
  // Panel visibility and sizing
  const [isAgentPanelVisible, setIsAgentPanelVisible] = useState(false);
  const [isAgentPanelMinimized, setIsAgentPanelMinimized] = useState(false);
  const [panelHeight, setPanelHeight] = useState(300);
  const [isResizing, setIsResizing] = useState(false);

  // Reset minimized state when panel is closed
  useEffect(() => {
    if (!isAgentPanelVisible) {
      setIsAgentPanelMinimized(false);
    }
  }, [isAgentPanelVisible]);

  // Job status display
  const [jobStatus, setJobStatus] = useState<JobStatus>('idle');
  const [progressMessage, setProgressMessage] = useState('Ready');

  // Global logs
  const [agentLogs, setAgentLogs] = useState<AgentLogEntry[]>([]);

  // Streaming text (live agent "thinking")
  const [streamingText, setStreamingText] = useState<string>('');
  const streamingTextRef = useRef<string>('');

  // Persisted ephemeral entries (previous streaming texts, now collapsible)
  const [persistedEphemeralEntries, setPersistedEphemeralEntries] = useState<PersistedEphemeralEntry[]>([]);

  // Per-pipeline logs and streaming
  const [pipelineLogs, setPipelineLogs] = useState<Record<string, AgentLogEntry[]>>({});
  const [pipelineStreamingText, setPipelineStreamingText] = useState<Record<string, string>>({});
  const pipelineStreamingTextRef = useRef<Record<string, string>>({});
  const [pipelinePersistedEphemeral, setPipelinePersistedEphemeral] = useState<Record<string, PersistedEphemeralEntry[]>>({});

  // Pipeline tabs
  const [activePipelineTab, setActivePipelineTab] = useState<string>('');
  const [parallelPipelines, setParallelPipelines] = useState<string[]>([]);

  // Historical job tabs
  const [historicalJobTabs, setHistoricalJobTabs] = useState<Array<{ jobId: string; title: string }>>([]);
  const [historicalJobLogs, setHistoricalJobLogs] = useState<Record<string, AgentLogEntry[]>>({});

  // Auto-scroll refs
  const logsContainerRef = useRef<HTMLDivElement>(null);
  const isUserNearBottomRef = useRef(true);

  // Filtered logs based on active tab
  const filteredAgentLogs = useMemo(() => {
    if (activePipelineTab.startsWith('history-')) {
      const jobId = activePipelineTab.replace('history-', '');
      return historicalJobLogs[jobId] || [];
    }
    if (activePipelineTab && pipelineLogs[activePipelineTab]) {
      return pipelineLogs[activePipelineTab];
    }
    // Fallback: if no tab selected or tab not found, show agentLogs
    return agentLogs;
  }, [activePipelineTab, agentLogs, pipelineLogs, historicalJobLogs]);

  // Auto-scroll when new content arrives — use rAF to avoid layout jitter
  const scrollRafRef = useRef<number>(0);
  useEffect(() => {
    if (!logsContainerRef.current || !isUserNearBottomRef.current) return;
    cancelAnimationFrame(scrollRafRef.current);
    scrollRafRef.current = requestAnimationFrame(() => {
      if (logsContainerRef.current) {
        logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
      }
    });
  }, [filteredAgentLogs, streamingText, persistedEphemeralEntries, pipelineStreamingText, pipelinePersistedEphemeral, activePipelineTab]);

  const handleLogsScroll = useCallback(() => {
    if (logsContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = logsContainerRef.current;
      isUserNearBottomRef.current = scrollHeight - scrollTop - clientHeight < 100;
    }
  }, []);

  /** Add a log entry to global logs, optionally also to a specific pipeline. */
  const addLog = useCallback((level: AgentLogEntry['level'], message: string, stage?: string, pipelineId?: string) => {
    const logEntry: AgentLogEntry = {
      id: Date.now(),
      timestamp: new Date(),
      level,
      stage,
      message
    };
    setAgentLogs((prev) => [...prev, logEntry]);
    if (pipelineId) {
      setPipelineLogs((prev) => ({
        ...prev,
        [pipelineId]: [...(prev[pipelineId] || []), logEntry]
      }));
    }
  }, []);

  /** Add a log to a specific pipeline tab (also adds prefixed entry to global). */
  const addPipelineLog = useCallback((pipelineId: string, level: AgentLogEntry['level'], message: string, stage?: string) => {
    const logEntry: AgentLogEntry = {
      id: Date.now(),
      timestamp: new Date(),
      level,
      stage,
      message
    };

    // Dynamically add pipeline tab if not present
    setParallelPipelines((prev) => (prev.includes(pipelineId) ? prev : [...prev, pipelineId]));

    setPipelineLogs((prev) => ({
      ...prev,
      [pipelineId]: [...(prev[pipelineId] || []), logEntry]
    }));

    setAgentLogs((prev) => [...prev, { ...logEntry, message: `[${pipelineId}] ${message}` }]);
  }, []);

  /** Initialize pipeline tabs and logs for a set of pipeline names. */
  const initializePipelineTabs = useCallback((pipelineNames: string[]) => {
    setParallelPipelines((prev) => {
      const newPipelines = pipelineNames.filter((name) => !prev.includes(name));
      return [...prev, ...newPipelines];
    });
    setPipelineLogs((prev) => {
      const updated = { ...prev };
      pipelineNames.forEach((name: string) => {
        if (!updated[name]) {
          updated[name] = [];
        }
      });
      return updated;
    });
    // Default to first pipeline tab
    if (pipelineNames.length > 0) {
      setActivePipelineTab(pipelineNames[0]);
    }
  }, []);

  /** Persist current streaming text as a collapsible ephemeral entry. */
  const persistStreamingText = useCallback(() => {
    if (streamingTextRef.current && streamingTextRef.current.trim().length > 0) {
      const persistedEntry: PersistedEphemeralEntry = {
        id: Date.now(),
        timestamp: new Date(),
        text: streamingTextRef.current.trim(),
        isExpanded: false
      };
      setPersistedEphemeralEntries((prev) => [...prev, persistedEntry]);
      streamingTextRef.current = '';
    }
    setStreamingText('');
  }, []);

  return {
    // Panel
    isAgentPanelVisible,
    setIsAgentPanelVisible,
    isAgentPanelMinimized,
    setIsAgentPanelMinimized,
    panelHeight,
    setPanelHeight,
    isResizing,
    setIsResizing,
    // Status
    jobStatus,
    setJobStatus,
    progressMessage,
    setProgressMessage,
    // Logs
    agentLogs,
    setAgentLogs,
    addLog,
    addPipelineLog,
    filteredAgentLogs,
    // Streaming
    streamingText,
    setStreamingText,
    streamingTextRef,
    persistedEphemeralEntries,
    setPersistedEphemeralEntries,
    persistStreamingText,
    // Pipeline-specific
    pipelineLogs,
    setPipelineLogs,
    pipelineStreamingText,
    setPipelineStreamingText,
    pipelineStreamingTextRef,
    pipelinePersistedEphemeral,
    setPipelinePersistedEphemeral,
    // Tabs
    activePipelineTab,
    setActivePipelineTab,
    parallelPipelines,
    setParallelPipelines,
    initializePipelineTabs,
    // Historical
    historicalJobTabs,
    setHistoricalJobTabs,
    historicalJobLogs,
    setHistoricalJobLogs,
    // Scroll
    logsContainerRef,
    handleLogsScroll
  };
};
